import {
  managedFeatureForRoute as contractManagedFeatureForRoute,
  managedFeatureIdSchema,
  type ManagedFeatureId,
} from '@kop/contracts';
import { workspaceFeatureMounted } from './workspace-route-policy';

export type FeatureAccessProjectionStatus =
  | Readonly<{ kind: 'absent' }>
  | Readonly<{ kind: 'legacy' | 'shadow'; version: number }>
  | Readonly<{ kind: 'enforced'; version: number; features: ReadonlySet<ManagedFeatureId> }>
  | Readonly<{ kind: 'malformed'; reason: string }>;

const selfServiceRoutes = new Set(['my-info']);

export function managedFeatureForRoute(routeId: string): ManagedFeatureId | null {
  const managed = contractManagedFeatureForRoute(routeId);
  return managed && workspaceFeatureMounted(routeId) && managed !== 'safety-review' ? managed : null;
}

export function isIndependentSelfServiceRoute(routeId: string): boolean {
  return selfServiceRoutes.has(routeId) && workspaceFeatureMounted(routeId);
}

export function featureAccessProjectionStatus(
  featureAccess: unknown,
): FeatureAccessProjectionStatus {
  if (featureAccess === undefined) return { kind: 'absent' };
  if (!featureAccess || typeof featureAccess !== 'object')
    return { kind: 'malformed', reason: 'projection must be an object' };
  const value = featureAccess as Record<string, unknown>;
  if (!['legacy', 'shadow', 'enforced'].includes(String(value.mode)))
    return { kind: 'malformed', reason: 'projection mode is invalid' };
  if (!Number.isSafeInteger(value.version) || Number(value.version) <= 0)
    return { kind: 'malformed', reason: 'projection version must be a positive integer' };
  if (!Array.isArray(value.features))
    return { kind: 'malformed', reason: 'projection features must be an array' };
  const features: ManagedFeatureId[] = [];
  for (const candidate of value.features) {
    const parsed = managedFeatureIdSchema.safeParse(candidate);
    if (!parsed.success) return { kind: 'malformed', reason: 'projection contains an unknown feature' };
    if (!features.includes(parsed.data)) features.push(parsed.data);
  }
  const mode = value.mode as 'legacy' | 'shadow' | 'enforced';
  return mode === 'enforced'
    ? { kind: 'enforced', version: Number(value.version), features: new Set(features) }
    : { kind: mode, version: Number(value.version) };
}
